import CircleIcon from "@mui/icons-material/Circle";
import {
  Box,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import Flex from "../../../../../../components/Flex";
import Text from "../../../../../../components/Text";
import { formatDate } from "../../../../../../utils/date-time-utility";
import { PAYMENT_POLICY_SECTIONS } from "../new-intake-form-contant/intake-constant";
import OutlinedCard from "../../../../../../components/OutlineCard";

const bulletListSx = {
  "& .MuiListItem-root": {
    alignItems: "flex-start",
    gap: 1,
    py: 0,
  },
  "& .MuiListItemIcon-root": {
    minWidth: "auto",
    mt: 1,
    "& .MuiSvgIcon-root": {
      fontSize: "8px",
    },
  },
};

const BulletList = ({ items }: { items: string[] }) => (
  <List sx={bulletListSx}>
    {items.map((item, index) => (
      <ListItem key={index}>
        <ListItemIcon>
          <CircleIcon />
        </ListItemIcon>
        <ListItemText
          primary={
            <Text variant="body2" color="textPrimary">
              {item}
            </Text>
          }
        />
      </ListItem>
    ))}
  </List>
);

interface PatientPaymentPoliciesProps {
  values: any;
}

const PatientPaymentPolicies = ({ values }: PatientPaymentPoliciesProps) => {
  const patientName =
    values?.paymentPolicyPatientName ||
    [values?.firstName, values?.lastName].filter(Boolean).join(" ");
  const dob = values?.paymentPolicyDob ?? values?.birthDate;
  const date = values?.paymentPolicyDate;
  const printedName =
    values?.paymentPolicyPrintedName ||
    [values?.firstName, values?.lastName].filter(Boolean).join(" ");
  const signature = values?.paymentPolicySign;

  return (
    <Box mt={0.5} id={"Payment Policies"}>
      <Text variant="subtitle1" fontWeight={600} mb={2}>
        Payment Policy & Financial Responsibility Consent Form
      </Text>

      <OutlinedCard>
        <Grid container spacing={2} mb={2}>
          <Grid item xs={12} sm={4}>
            <Text variant="body2" color="textSecondary">
              Patient Name
            </Text>
            <Text variant="body2">{patientName || "-"}</Text>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Text variant="body2" color="textSecondary">
              Date of Birth
            </Text>
            <Text variant="body2">{formatDate(dob) || "-"}</Text>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Text variant="body2" color="textSecondary">
              Date
            </Text>
            <Text variant="body2">{formatDate(date) || "-"}</Text>
          </Grid>
        </Grid>
        <Flex flexDirection="column" gap={2}>
          {PAYMENT_POLICY_SECTIONS.map((section) => (
            <Box key={section.title}>
              <Text variant="subtitle1" fontWeight={600} mb={0.5}>
                {section.title}
              </Text>
              {section.body && (
                <Text variant="body2" color="textPrimary">
                  {section.body}
                </Text>
              )}
              {section.bullets && <BulletList items={section.bullets} />}
              {section.footer && <Text>{section.footer}</Text>}
            </Box>
          ))}
        </Flex>

        <Box mt={3}>
          <Text variant="subtitle2" fontWeight={600} mb={1}>
            Acknowledgment & Consent
          </Text>
          <Text variant="body2" color="textPrimary" mb={2}>
            I have read, understood, and agree to the Payment Policy and
            Financial Responsibility terms outlined above. I acknowledge that I
            am responsible for all charges not covered by my insurance.
          </Text>

          <Grid container spacing={2} mb={2}>
            <Grid item xs={12} sm={6}>
              <Text variant="body2" color="textSecondary">
                Printed Name
              </Text>
              <Text variant="body2">{printedName || "-"}</Text>
            </Grid>
          </Grid>

          {signature && (
            <Box mt={2}>
              <Text mb={1} fontWeight="medium">
                Patient/Responsible Party Signature
              </Text>
              <img
                src={signature}
                alt="Payment Policy Signature"
                style={{
                  height: "150px",
                  width: "368px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                }}
              />
            </Box>
          )}
        </Box>
      </OutlinedCard>
    </Box>
  );
};

export default PatientPaymentPolicies;
